import { Table, TableBody, TableCell, TableHead, TableRow, Select, MenuItem , TextField} from '@mui/material';
import { Pagination } from 'antd';
import Loading from 'components/Loading';
import { isEmpty } from 'lodash';
import React, { memo, useMemo, useState } from 'react';
import styled from 'styled-components';
import TeacherSupportTableRow from './TeacherSupportTableRow';
import TeacherSupportPrint from './TeacherSupportPrint';
import SentryWrap from 'components/Sentry';

const tableHeader = [
  { id: 1, headerText: 'Sequence No.' },
  { id: 2, headerText: 'Name' },
  { id: 3, headerText: 'District Name' },
  { id: 4, headerText: 'Date' },
  { id: 5, headerText: 'Status' },
  { id: 6, headerText: 'Notes' },
];

const TeacherSupportModuleComponent = ({ teacherSupportData, loading, onUpdateItem }) => {
  const [statusFilter, setStatusFilter] = useState('All');
   const [searchTerm, setSearchTerm] = useState('');
  const [dateSortOrder, setDateSortOrder] = useState('desc');
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 30,
    total: 0,
    pageSizeOptions: ['30', '50', '100'],
  });

  const sortedTeacherSupportData = useMemo(() => {
    return [...teacherSupportData].sort((a, b) => {
      const dateA = new Date(a.date_time || 0);
      const dateB = new Date(b.date_time || 0);
      if (dateSortOrder === 'asc') {
        return dateA - dateB;
      }
      return dateB - dateA;
    });
  }, [teacherSupportData, dateSortOrder]);

  const filteredTeacherSupportData = useMemo(() => {
    const filterStatus = statusFilter.toLowerCase().replace(/[-\s]/g, '');
    const search = searchTerm.trim().toLowerCase();
    return sortedTeacherSupportData.filter((item) => {
      if (filterStatus !== 'all') {
        const itemStatus = (item.status || '').toLowerCase().replace(/[-\s]/g, '');
        if (itemStatus !== filterStatus) {
          return false;
        }
      }
      if (search) {
        const teacherName = String(item.teacher || item.teacher_name || item.teacherName || '').toLowerCase().trim();
        const districtName = String(item.district || item.district_name || item.districtName || '').toLowerCase().trim();
        
        const searchWords = search.split(/\s+/).filter(word => word.length > 0);
        
        const exactTeacherMatch = teacherName === search;
        const exactDistrictMatch = districtName === search;
        
        const teacherNameWords = teacherName.split(/\s+/);
        const districtNameWords = districtName.split(/\s+/);
        
        const teacherPartialMatch = searchWords.every(searchWord => 
          teacherNameWords.some(nameWord => nameWord.includes(searchWord))
        );
        const districtPartialMatch = searchWords.every(searchWord => 
          districtNameWords.some(nameWord => nameWord.includes(searchWord))
        );
        
        return exactTeacherMatch || exactDistrictMatch || teacherPartialMatch || districtPartialMatch;
      }
      return true;
    });
  }, [sortedTeacherSupportData, statusFilter, searchTerm]);


  const paginatedTeacherSupport = useMemo(() => {
    setPagination((prev) => ({
      ...prev,
      total: filteredTeacherSupportData.length,
    }));

    const startIndex = (pagination.current - 1) * pagination.pageSize;
    const endIndex = startIndex + pagination.pageSize;
    return filteredTeacherSupportData.slice(startIndex, endIndex);
  }, [filteredTeacherSupportData, pagination.current, pagination.pageSize]);

  const handlePageChange = (page, pageSize) => {
    setPagination((prev) => ({
      ...prev,
      current: page,
      pageSize: pageSize || prev.pageSize,
    }));
  };

  const handlePageSizeChange = (current, size) => {
    setPagination((prev) => ({
      ...prev,
      current: 1,
      pageSize: size,
    }));
  };


  return (
    <SentryWrap>
    <NoPaddingWrapper>
      <TableContainer>
        <TableHeader>
          <HeaderTitle>District Name or all users</HeaderTitle>
          <HeaderActions>
            <StatusFilterWrapper>
              <FilterLabel>Status:</FilterLabel>
              <Select
                value={statusFilter}
                onChange={(e) => {
                  setStatusFilter(e.target.value);
                  setPagination((prev) => ({ ...prev, current: 1 }));
                }}
                variant="outlined"
                size="small"
                sx={{
                  backgroundColor: 'white',
                  minWidth: 150,
                  height: 32,
                  borderRadius: '20px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',

                  '& .MuiSelect-select': {
                    padding: 0,
                    lineHeight: '32px',
                    fontSize: '12px',
                    textAlign: 'center',
                  },

                  '& .MuiOutlinedInput-notchedOutline': {
                    border: 'none',
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    border: 'none',
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    border: 'none',
                  },
                }}
              >
                <MenuItem value="All">All</MenuItem>
                <MenuItem value="Not started">Not started</MenuItem>
                <MenuItem value="In progress">In progress</MenuItem>
                <MenuItem value="Under review">Under review</MenuItem>
                <MenuItem value="Completed">Completed</MenuItem>
              </Select>
            </StatusFilterWrapper>
            <StatusFilterWrapper>
              <FilterLabel>Date:</FilterLabel>
              <Select
                value={dateSortOrder}
                onChange={(e) => {
                  setDateSortOrder(e.target.value);
                  setPagination((prev) => ({ ...prev, current: 1 }));
                }}
                variant="outlined"
                size="small"
                sx={{
                  backgroundColor: 'white',
                  minWidth: 150,
                  height: 32,
                  borderRadius: '20px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',

                  '& .MuiSelect-select': {
                    padding: 0,
                    lineHeight: '32px',
                    fontSize: '12px',
                    textAlign: 'center',
                  },

                  '& .MuiOutlinedInput-notchedOutline': {
                    border: 'none',
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    border: 'none',
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    border: 'none',
                  },
                }}
              >
                <MenuItem value="desc">Descending</MenuItem>
                <MenuItem value="asc">Ascending</MenuItem>
              </Select>
            </StatusFilterWrapper>
            <SearchInput
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setPagination((prev) => ({ ...prev, current: 1 }));
              }}
              variant="outlined"
              size="small"
              placeholder="Search by teacher or district"
              InputProps={{
                sx: {
                  height: 32,
                  padding: '0 12px !important',
                  borderRadius: '20px',
                  backgroundColor: 'white',

                  '& .MuiOutlinedInput-input': {
                    padding: 0,
                    lineHeight: '28px',
                    fontSize: '12px',
                  },

                  '& .MuiOutlinedInput-notchedOutline': {
                    border: 'none',
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    border: 'none',
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    border: 'none',
                  },
                },
              }}
            />
            <TeacherSupportPrint data={filteredTeacherSupportData} />
          </HeaderActions>
        </TableHeader>
        
        <Table
          style={{
            width: '100%',
            textAlign: 'center',
            backgroundColor: '#fff',
          }}
        >
          <TableHead>
            <TableRow>
              {tableHeader.map((item) => (
                <StyledTableHead key={item.id}>{item?.headerText}</StyledTableHead>
              ))}
            </TableRow>
          </TableHead>

          {loading ? (
            <TableBody>
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <div style={{ padding: '20px 0' }}>
                    <Loading size={30} />
                  </div>
                </TableCell>
              </TableRow>
            </TableBody>
          ) : (
            <TableBody>
              {isEmpty(paginatedTeacherSupport) && (
                <TableRow key={'0'} style={{ border: 0 }}>
                  <TableCell colSpan={6} style={{ textAlign: 'center' }}>
                    No teacher support available
                  </TableCell>
                </TableRow>
              )}

              {paginatedTeacherSupport?.map((element, index) => (
                <TeacherSupportTableRow
                  key={element.id || `${element.teacher}-${index}`}
                  row={element}
                  sequenceNo={(pagination.current - 1) * pagination.pageSize + index + 1}
                  onUpdateItem={onUpdateItem}
                />
              ))}

              {!isEmpty(filteredTeacherSupportData) && (
                <TableRow>
                  <TableCell colSpan={6} align="right">
                    <Pagination
                      current={pagination.current}
                      pageSize={pagination.pageSize}
                      total={pagination.total}
                      onChange={handlePageChange}
                      showSizeChanger={true}
                      pageSizeOptions={pagination.pageSizeOptions}
                      onShowSizeChange={handlePageSizeChange}
                      showQuickJumper={true}
                      showTotal={(total, range) => `${range[0]}-${range[1]} of ${total} items`}
                      itemRender={(page, type, originalElement) => {
                        if (type === 'page' && page === pagination.current) {
                          return (
                            <div className="ant-pagination-item-active custom-active-page">
                              {page}
                            </div>
                          );
                        }
                        return originalElement;
                      }}
                    />
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          )}
        </Table>
      </TableContainer>
    </NoPaddingWrapper>
    </SentryWrap>
  );
};

export default memo(TeacherSupportModuleComponent);

const NoPaddingWrapper = styled.div`
  margin: -24px;
  height: calc(100vh - 64px);
  overflow: auto;
`;

const TableContainer = styled.div`
  background: white;
`;

const TableHeader = styled.div`
  background: #1e3a8a;
  padding: 16px;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const HeaderTitle = styled.h1`
  color: white;
  font-size: 20px;
  font-weight: 600;
  margin: 0;
`;

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const StatusFilterWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const FilterLabel = styled.span`
  color: white;
  font-size: 14px;
  font-weight: 500;
`;

const StyledTableCell = styled(TableCell)`
  text-align: left;
  font-size: 14px;
  padding: 12px 16px;
  border-bottom: 1px solid #e5e7eb;
`;

const StyledTableHead = styled(TableCell)`
  text-align: left;
  font-size: 14px;
  font-weight: 600;
  padding: 12px 16px;
  background-color: #dbeafe;
  color: #374151;
`;

const SearchInput = styled(TextField)`
  background-color: white;
  min-width: 220px;
  height: 32px;
  border-radius: 20px;
  padding: 0 12px;
  '& .MuiOutlinedInput-notchedOutline': {
    border: 'none',
  },
`;


